import React, { useState, useEffect } from "react";
import { get } from "lodash";
import { fetchElectionComment, postElectionReaction } from "../../../api";
import styles from "./style.m.css";
import Icon from "../../atoms/icon";
import { Link } from "@quintype/components";
import { connect } from "react-redux";
import { DeepBI } from "../../../integrations";
import LoginMobile from "../header/user-login/login-mobile";
import useElectionUser from "../../rows/app-webview-userID/index";

const sign = require("jwt-encode");

const truncateContent = (content, maxLength) => {
  if (content.length <= maxLength) return content;
  return content.slice(0, maxLength) + "...";
};

const TnElectionCommentsWidget = props => {
  const [comments, setComments] = useState([]);
  const [offset, setOffset] = useState(0);
  const [totalComments, setTotalComments] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [reactionLoading, setReactionLoading] = useState({});
  const [IsOpened, setIsOpened] = useState(false);
  const COMMENTS_PER_PAGE = 30;
  const jwtKey = get(props, ["jwtKey"], "");

  const { loggedOut, UserId, isAppView, isReady } = useElectionUser();

  function generateJWT(payload) {
    return sign(payload, jwtKey);
  }

  const fetchComments = (pageOffset = 0, userId = UserId) => {
    setIsLoading(true);
    const PartyData = { _tn: Date.now() };
    fetchElectionComment(generateJWT(PartyData), "all", pageOffset, COMMENTS_PER_PAGE, userId)
      .then(res => {
        const newComments = get(res, ["data", "items", "data"], []);
        const total = get(res, ["data", "items", "total"], 0);

        if (pageOffset === 0) {
          setComments(newComments);
        } else {
          setComments(prevComments => [...prevComments, ...newComments]);
        }

        setTotalComments(total);
        setOffset(pageOffset + COMMENTS_PER_PAGE);
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  };

  // Runs when UserId is resolved from hook (handles both web and app)
  useEffect(
    () => {
      if (!isReady) return;
      fetchComments(0, UserId);
    },
    [isReady, UserId]
  );

  const loadMore = () => {
    if (!isLoading) {
      fetchComments(offset);
    }
  };

  const toggleCommentExpansion = commentIndex => {
    setComments(prevComments =>
      prevComments.map(
        (comment, index) => (index === commentIndex ? { ...comment, expanded: !comment.expanded } : comment)
      )
    );
  };

  const handleReaction = (commentId, reactionType, commentIndex) => {
    if (loggedOut) {
      setIsOpened(true);
      DeepBI.PingLoginevents("Login_widget_view", "", "", "", "", "election_like_Comment");
      return;
    }

    const reactionKey = `${commentId}_${reactionType}`;
    if (reactionLoading[reactionKey]) return;

    const comment = comments[commentIndex];
    const currentReaction = comment.liked ? "liked" : comment.disliked ? "disliked" : null;

    const isTogglingOff = currentReaction === reactionType;
    const apiStatus = isTogglingOff ? 0 : reactionType === "liked" ? 1 : 2;

    const previousComment = { ...comment };

    setComments(prevComments =>
      prevComments.map((c, i) => {
        if (i !== commentIndex) return c;
        let likesDelta = 0;
        let dislikesDelta = 0;

        if (isTogglingOff) {
          if (reactionType === "liked") likesDelta = -1;
          if (reactionType === "disliked") dislikesDelta = -1;
        } else if (reactionType === "liked") {
          likesDelta = 1;
          if (currentReaction === "disliked") dislikesDelta = -1;
        } else {
          dislikesDelta = 1;
          if (currentReaction === "liked") likesDelta = -1;
        }

        return {
          ...c,
          like_count: get(c, ["like_count"], 0) + likesDelta,
          dislike_count: get(c, ["dislike_count"], 0) + dislikesDelta,
          liked: isTogglingOff ? false : reactionType === "liked",
          disliked: isTogglingOff ? false : reactionType === "disliked"
        };
      })
    );

    setReactionLoading(prev => ({ ...prev, [reactionKey]: true }));

    const formData = { pblm_id: commentId, id: UserId, status: apiStatus };

    postElectionReaction(generateJWT(formData))
      .then(response => {
        if (response && response.data && response.data.success) {
          fetchComments(0);
        }
      })
      .catch(err => {
        console.error("Reaction post failed:", err);
        setComments(prevComments => prevComments.map((c, i) => (i !== commentIndex ? c : previousComment)));
      })
      .finally(() => {
        setReactionLoading(prev => {
          const newState = { ...prev };
          delete newState[reactionKey];
          return newState;
        });
      });
  };

  const buildConstituencyHref = comment => {
    const slug = get(comment, ["thokuthi_slug"], "").toLowerCase();
    let href = `/election/tn-assembly-election-2026/constituency/${slug}`;

    if (typeof window !== "undefined" && window.location) {
      const params = new URLSearchParams(window.location.search);
      if (params.get("source") === "app") {
        // Forward all params as-is: source=app&id=xxx&utype=P&deviceid=xxx
        href += `?${params.toString()}`;
      }
    }

    return href;
  };

  const handleMobileLoginClose = () => {
    setIsOpened(false);
  };

  const buildShareHref = comment => {
    const slug = get(comment, ["thokuthi_slug"], "").toLowerCase();
    let href = `/election/tn-assembly-election-2026/constituency/${slug}`;
    const domainName = global && global.location && global.location.origin;

    if (typeof window !== "undefined" && window && window.location) {
      const params = new URLSearchParams(window.location.search);
      if (params.get("source") === "app") {
        href += "?source=app";
      }
      // Always prepend origin for full URL
      return `${domainName}${href}`;
    }

    return href;
  };

  const handleShare = async comment => {
    const fullUrl = buildShareHref(comment);

    if (navigator.share) {
      await navigator.share({
        title: comment.title || "Constituency",
        url: fullUrl
      });
    } else {
      await navigator.clipboard.writeText(fullUrl);
    }
  };

  // Show interactive elements only if:
  // - Normal web user (not isAppView) OR
  // - App user with valid UserId
  const showInteractions = !isAppView || !!UserId;

  return (
    <div className={`${styles["election-container"]} container`}>
      {IsOpened && <LoginMobile onClose={handleMobileLoginClose} />}

      {comments &&
        comments.length > 0 && (
          <div className={styles["comments-wrapper"]}>
            <div className={styles["comments-wrapper-section"]}>
              <h1 className={styles["comments-headline"]}>
                <span>தொகுதியின் நிலை - மக்கள் குரல்</span>
              </h1>
              <div className={styles["comments-height"]}>
                {comments.map((comment, index) => {
                  const commentId = get(comment, ["pblm_id"], index);
                  const userReaction = comment.liked ? "liked" : comment.disliked ? "disliked" : null;
                  const likes = get(comment, ["like_count"], 0);
                  const dislikes = get(comment, ["dislike_count"], 0);

                  return (
                    <ul key={commentId} className={styles["comments-section"]}>
                      <li className={styles["comments-header"]}>
                        <Icon type="icon-user" />
                        <div>
                          <h2>{get(comment, ["user_name"], "")}</h2>
                          <h3>
                            <Link href={buildConstituencyHref(comment)}>{get(comment, ["area"], "")}</Link>
                          </h3>
                        </div>
                      </li>

                      <li className={styles["comments-pblm"]}>
                        {comment.expanded ? (
                          <div>
                            {get(comment, ["thokuthi_pblm"], "")}
                            <button onClick={() => toggleCommentExpansion(index)} className={styles["read-more-less"]}>
                              Read Less
                            </button>
                          </div>
                        ) : (
                          <div>
                            {truncateContent(get(comment, ["thokuthi_pblm"], ""), 200)}
                            {get(comment, ["thokuthi_pblm"], "").length > 200 && (
                              <button
                                onClick={() => toggleCommentExpansion(index)}
                                className={styles["read-more-less"]}
                              >
                                Read More
                              </button>
                            )}
                          </div>
                        )}
                      </li>

                      {showInteractions && (
                        <li className={`${styles["comments-reactions"]}`}>
                          <div
                            className={`${styles["reaction-btn"]} ${
                              userReaction === "liked" ? styles["reaction-active"] : ""
                            } ${styles["like-btns"]}`}
                            onClick={() => handleReaction(commentId, "liked", index)}
                            aria-label="Like"
                          >
                            <Icon type="like" />
                            <span className={styles["reaction-count"]}>{likes}</span>
                          </div>

                          <div
                            className={`${styles["reaction-btn"]} ${
                              userReaction === "disliked" ? styles["reaction-active"] : ""
                            }`}
                            onClick={() => handleReaction(commentId, "disliked", index)}
                            aria-label="Dislike"
                          >
                            <Icon type="unlike" />
                            <span className={`${styles["reaction-count"]} ${styles["dislike-count"]}`}>{dislikes}</span>
                          </div>

                          <div className={`${styles["reaction-btn"]} ${styles["reaction-share-icon"]}`}>
                            <div className={styles["reaction-btn"]} onClick={() => handleShare(comment)}>
                              <Icon type="icon-comments-share" />
                            </div>
                          </div>
                        </li>
                      )}
                    </ul>
                  );
                })}
              </div>

              {comments.length < totalComments && (
                <div className={styles["load-more-wrapper"]}>
                  <button
                    onClick={loadMore}
                    className={styles["load-more"]}
                    disabled={isLoading}
                    style={{ opacity: isLoading ? 0.6 : 1, cursor: isLoading ? "wait" : "pointer" }}
                  >
                    {isLoading ? <div>Loading...</div> : "Load More"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

function mapStateToProps(state) {
  return { jwtKey: get(state, ["qt", "config", "JwtKey"]) };
}

export default connect(mapStateToProps)(TnElectionCommentsWidget);
